import React, { useEffect, useState } from "react";
import { api, fmtCurrency, hasAnyRole, formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Switch } from "@/components/ui/switch";
import { downloadReport } from "@/lib/pdf";
import {
  Plus, Pencil, Trash2, Package, UtensilsCrossed, Save, X, Search, FileDown,
  Utensils, Soup, Salad, IceCream, Wine, Cookie, Beef, Fish, CakeSlice, Coffee, Pizza,
} from "lucide-react";

// Lucide icon picker palette for categories. Keys must match backend payload "icon" values.
const CAT_ICONS = { Utensils, Soup, Salad, IceCream, Wine, Cookie, Beef, Fish, CakeSlice, Coffee, Pizza };
const CAT_ICON_KEYS = Object.keys(CAT_ICONS);
export function CatIcon({ name, className = "w-3.5 h-3.5" }) {
  const Cmp = CAT_ICONS[name] || Utensils;
  return <Cmp className={className} />;
}

export default function MenuPage() {
  const { user } = useAuth();
  const canManage = hasAnyRole(user, ["admin", "operations"]);
  const [tab, setTab] = useState("items");
  return (
    <div className="space-y-6" data-testid="menu-page">
      <div>
        <div className="label-eyebrow">Catalog</div>
        <h1 className="font-display text-4xl mt-1">Menu & Packages</h1>
        <p className="text-sm text-zinc-500 mt-1">Master list of dishes and reusable packages used across all orders.</p>
      </div>
      <div className="flex gap-2 border-b border-[#1a1a1a]">
        <button onClick={() => setTab("items")} data-testid="menu-tab-items" className={`px-4 py-2 text-sm transition-colors ${tab === "items" ? "text-[#D4AF37] border-b-2 border-[#D4AF37]" : "text-zinc-500 hover:text-zinc-300"}`}>
          <UtensilsCrossed className="w-3.5 h-3.5 inline mr-1.5" /> Menu Items
        </button>
        <button onClick={() => setTab("packages")} data-testid="menu-tab-packages" className={`px-4 py-2 text-sm transition-colors ${tab === "packages" ? "text-[#D4AF37] border-b-2 border-[#D4AF37]" : "text-zinc-500 hover:text-zinc-300"}`}>
          <Package className="w-3.5 h-3.5 inline mr-1.5" /> Packages
        </button>
      </div>
      {tab === "items" ? <ItemsTab canManage={canManage} /> : <PackagesTab canManage={canManage} />}
    </div>
  );
}

function ItemsTab({ canManage }) {
  const [items, setItems] = useState([]);
  const [categories, setCategories] = useState([]);
  const [editing, setEditing] = useState(null);
  const [q, setQ] = useState("");
  const [catFilter, setCatFilter] = useState("");
  const reload = async () => {
    const [ri, rc] = await Promise.all([api.get("/menu/items"), api.get("/menu/categories")]);
    setItems(ri.data || []);
    setCategories(rc.data || []);
  };
  useEffect(() => { reload(); }, []);

  const save = async (it) => {
    try {
      if (it.id) await api.patch(`/menu/items/${it.id}`, it);
      else await api.post("/menu/items", it);
      toast.success(it.id ? "Item updated" : "Item added");
      setEditing(null); await reload();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Save failed"); }
  };
  const remove = async (it) => {
    if (!window.confirm(`Delete "${it.name}" from catalog?`)) return;
    try { await api.delete(`/menu/items/${it.id}`); toast.success("Deleted"); await reload(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Delete failed"); }
  };
  const quickToggle = async (it) => {
    const next = !(it.alacarte_active !== false);
    try {
      await api.patch(`/menu/items/${it.id}`, { alacarte_active: next });
      toast.success(next ? `"${it.name}" is now available à la carte` : `"${it.name}" hidden from à la carte (packages unaffected)`);
      await reload();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Toggle failed"); }
  };

  // Group items by category key (after search + category filter)
  const ql = q.trim().toLowerCase();
  const visible = (ql
    ? items.filter((it) =>
        (it.name || "").toLowerCase().includes(ql) ||
        (it.name_cn || "").includes(q.trim()) ||
        (it.category || "").toLowerCase().includes(ql))
    : items
  ).filter((it) => !catFilter || it.category === catFilter);
  const grouped = visible.reduce((acc, it) => {
    (acc[it.category] = acc[it.category] || []).push(it);
    return acc;
  }, {});

  // Render order: known categories (sort_order) first, then any orphaned keys
  const orderedKeys = [
    ...categories.map((c) => c.key).filter((k) => grouped[k]?.length),
    ...Object.keys(grouped).filter((k) => !categories.find((c) => c.key === k)),
  ];
  const catMap = Object.fromEntries(categories.map((c) => [c.key, c]));

  const downloadPdf = () => {
    const rows = orderedKeys.flatMap((k) =>
      (grouped[k] || []).map((it) => [
        it.name + (it.name_cn ? ` (${it.name_cn})` : ""),
        catMap[k]?.name_en || k,
        fmtCurrency(it.unit_price),
        (it.min_order_qty || 1) > 1 ? String(it.min_order_qty) : "—",
        (it.variants || []).join(" / ") || "—",
        it.is_active === false ? "Hidden" : "Active",
      ])
    );
    downloadReport({
      title: "Menu Catalog",
      filename: "menu-catalog.pdf",
      subtitle: [catFilter ? `Category: ${catMap[catFilter]?.name_en || catFilter}` : "All categories", ql ? `Search: "${q.trim()}"` : null].filter(Boolean).join(" · "),
      kpis: [
        { label: "Dishes", value: visible.length },
        { label: "Categories", value: orderedKeys.length },
        { label: "Active", value: visible.filter((i) => i.is_active !== false).length },
      ],
      sections: [{
        title: "Dishes",
        columns: ["Dish", "Category", "Price", "Min Qty", "Variants", "Status"],
        rows,
        columnStyles: { 0: { cellWidth: 62 }, 2: { halign: "right", cellWidth: 24 }, 3: { halign: "center", cellWidth: 18 } },
      }],
    });
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3 flex-wrap">
        <div className="relative flex-1 min-w-[220px]">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500 pointer-events-none" />
          <Input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search dish name / 中文 / category…"
            data-testid="menu-search"
            className="pl-9 bg-[#050505] border-[#2a2a2a]"
          />
        </div>
        <Button onClick={downloadPdf} variant="outline" data-testid="menu-items-pdf" className="border-[#2a2a2a] hover:border-[#D4AF37] hover:text-[#D4AF37] rounded-sm">
          <FileDown className="w-3.5 h-3.5 mr-1.5" /> Download PDF
        </Button>
        {canManage && (
          <Button onClick={() => setEditing({ name: "", name_cn: "", category: categories[0]?.key || "main", unit_price: 0, description: "", is_active: true })} data-testid="new-menu-item" className="bg-[#D4AF37] text-black hover:bg-[#E5C86B] rounded-sm">
            <Plus className="w-3.5 h-3.5 mr-1.5" /> New Menu Item
          </Button>
        )}
      </div>
      <div className="flex items-center gap-1.5 flex-wrap" data-testid="menu-cat-filter">
        <button onClick={() => setCatFilter("")} data-testid="menu-cat-filter-all"
          className={`px-3 py-1.5 text-xs rounded-sm border transition-colors ${!catFilter ? "bg-[#D4AF37] text-black border-[#D4AF37]" : "border-[#2a2a2a] text-zinc-400 hover:border-[#D4AF37]"}`}>All</button>
        {categories.map((c) => (
          <button key={c.key} onClick={() => setCatFilter(catFilter === c.key ? "" : c.key)} data-testid={`menu-cat-filter-${c.key}`}
            className={`px-3 py-1.5 text-xs rounded-sm border transition-colors ${catFilter === c.key ? "bg-[#D4AF37] text-black border-[#D4AF37]" : "border-[#2a2a2a] text-zinc-400 hover:border-[#D4AF37]"}`}>{c.name_en}</button>
        ))}
      </div>
      {(ql || catFilter) && visible.length === 0 && (
        <div className="text-sm text-zinc-500 py-8 text-center" data-testid="menu-search-empty">No dishes match your filter</div>
      )}
      {orderedKeys.map((catKey) => {
        const cat = catMap[catKey];
        const color = cat?.color || "#A1A1AA";
        return (
          <div key={catKey} className="card-lux p-6">
            <div className="flex items-center gap-2 mb-3">
              <span className="inline-flex items-center justify-center w-7 h-7 rounded-sm" style={{ background: `${color}1A`, color }}>
                <CatIcon name={cat?.icon} />
              </span>
              <div>
                <div className="label-eyebrow" style={{ color }}>{cat?.name_en || catKey}</div>
                {cat?.name_cn && <div className="text-[10px] text-zinc-500">{cat.name_cn}</div>}
              </div>
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-2">
              {grouped[catKey].map((it) => (
                <div key={it.id} className="flex items-center justify-between gap-2 p-3 rounded-sm bg-[#0a0a0a] border border-[#1a1a1a]" data-testid={`menu-item-${it.id}`}>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <span className="text-sm text-zinc-100 truncate">{it.name}</span>
                      {!it.is_active && <span className="text-[9px] uppercase text-zinc-600">hidden</span>}
                    </div>
                    <div className="text-[11px] text-zinc-500">{it.name_cn || "—"} · {fmtCurrency(it.unit_price)} / {it.uom || "pax"}</div>
                  </div>
                  {canManage && (
                    <div className="flex items-center gap-1.5">
                      <Switch checked={it.alacarte_active !== false} onCheckedChange={() => quickToggle(it)} title="À la carte availability (packages unaffected)" data-testid={`menu-item-toggle-${it.id}`} className="data-[state=checked]:bg-emerald-500 scale-90" />
                      <Button onClick={() => setEditing(it)} variant="outline" size="sm" className="border-[#2a2a2a] hover:border-[#D4AF37] hover:text-[#D4AF37] rounded-sm h-7 w-7 p-0" data-testid={`menu-item-edit-${it.id}`}><Pencil className="w-3 h-3" /></Button>
                      <Button onClick={() => remove(it)} variant="outline" size="sm" className="border-red-500/30 text-red-400 hover:bg-red-500/10 rounded-sm h-7 w-7 p-0" data-testid={`menu-item-delete-${it.id}`}><Trash2 className="w-3 h-3" /></Button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        );
      })}
      {editing && <ItemEditor item={editing} categories={categories} onCategoriesChanged={reload} onClose={() => setEditing(null)} onSave={save} />}
    </div>
  );
}

function ItemEditor({ item, categories = [], onCategoriesChanged, onClose, onSave }) {
  const [f, setF] = useState({
    id: item.id, name: item.name || "", name_cn: item.name_cn || "",
    category: item.category || (categories[0]?.key || "main"), unit_price: item.unit_price || 0,
    department: item.department || "hot_kitchen", min_order_qty: item.min_order_qty || 1,
    uom: item.uom || "pax",
    description: item.description || "", is_active: item.is_active !== false,
  });
  const [variantsText, setVariantsText] = useState((item.variants || []).join(" / "));
  const [catModal, setCatModal] = useState(null); // null | {} (new) | {id...} (edit)

  const onCategorySelectChange = (val) => {
    if (val === "__new__") { setCatModal({}); return; }
    setF({ ...f, category: val });
  };

  const saveCategory = async (c) => {
    try {
      const body = { name_en: c.name_en, name_cn: c.name_cn, color: c.color, icon: c.icon };
      if (c.id) {
        await api.patch(`/menu/categories/${c.id}`, body);
        toast.success("Category updated");
      } else {
        const r = await api.post("/menu/categories", body);
        toast.success("Category created");
        // Auto-select the newly-created category in the item form.
        if (r.data?.key) setF((prev) => ({ ...prev, category: r.data.key }));
      }
      setCatModal(null);
      onCategoriesChanged?.();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Save failed"); }
  };
  const deleteCategory = async (c) => {
    if (!window.confirm(`Delete category "${c.name_en}"? Built-in categories can't be deleted, and a category in use must be reassigned first.`)) return;
    try { await api.delete(`/menu/categories/${c.id}`); toast.success("Category deleted"); setCatModal(null); onCategoriesChanged?.(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Delete failed"); }
  };

  const currentCat = categories.find((c) => c.key === f.category);

  return (
    <Dialog open={true} onOpenChange={(v) => !v && onClose?.()}>
      <DialogContent className="bg-[#0a0a0a] border-[#2a2a2a] text-white">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl">{f.id ? "Edit Menu Item" : "New Menu Item"}</DialogTitle>
          <DialogDescription className="sr-only">Manage a single dish in the catalog.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <Field label="Name · English"><Input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} data-testid="mi-name" className="bg-[#050505] border-[#2a2a2a]" /></Field>
            <Field label="Name · 中文"><Input value={f.name_cn} onChange={(e) => setF({ ...f, name_cn: e.target.value })} data-testid="mi-name-cn" className="bg-[#050505] border-[#2a2a2a]" /></Field>
            <Field label="Category">
              <div className="flex items-stretch gap-1">
                <select value={f.category} onChange={(e) => onCategorySelectChange(e.target.value)} data-testid="mi-category" className="flex-1 bg-[#050505] border border-[#2a2a2a] rounded-sm px-3 h-10 text-sm">
                  {categories.map((c) => (<option key={c.key} value={c.key}>{c.name_en}{c.name_cn ? ` · ${c.name_cn}` : ""}</option>))}
                  {/* Orphan fallback for items whose category was removed */}
                  {f.category && !categories.find((c) => c.key === f.category) && (
                    <option value={f.category}>{f.category}</option>
                  )}
                  <option value="__new__">＋ New category…</option>
                </select>
                {currentCat && (
                  <Button type="button" onClick={() => setCatModal(currentCat)} variant="outline" data-testid="mi-category-edit" className="h-10 w-10 p-0 border-[#2a2a2a] hover:border-[#D4AF37] hover:text-[#D4AF37] rounded-sm" title="Edit this category">
                    <Pencil className="w-3.5 h-3.5" />
                  </Button>
                )}
              </div>
            </Field>
            <Field label="Unit Price (RM)"><Input type="number" step="0.01" value={f.unit_price} onChange={(e) => setF({ ...f, unit_price: Number(e.target.value) })} data-testid="mi-price" className="bg-[#050505] border-[#2a2a2a]" /></Field>
            <Field label="Unit (UOM)">
              <select value={f.uom} onChange={(e) => setF({ ...f, uom: e.target.value })} data-testid="mi-uom" className="w-full bg-[#050505] border border-[#2a2a2a] rounded-sm px-3 h-10 text-sm">
                <option value="pax">Pax</option>
                <option value="set">Set</option>
                <option value="pcs">Pcs</option>
              </select>
            </Field>
            <Field label="Kitchen Department">
              <select value={f.department} onChange={(e) => setF({ ...f, department: e.target.value })} data-testid="mi-department" className="w-full bg-[#050505] border border-[#2a2a2a] rounded-sm px-3 h-10 text-sm">
                <option value="hot_kitchen">Hot Kitchen</option>
                <option value="pastry">Pastry</option>
                <option value="cold_kitchen">Cold Kitchen</option>
                <option value="beverage">Beverage</option>
                <option value="general">General</option>
              </select>
            </Field>
          </div>
          <Field label="Description (optional)">
            <Input value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} placeholder="Short note for the kitchen" className="bg-[#050505] border-[#2a2a2a]" />
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Variants (optional — customer must pick one when ordering)">
              <Input value={variantsText} onChange={(e) => setVariantsText(e.target.value)} placeholder="e.g. Matcha Flavor / Chocolate Flavor" data-testid="mi-variants" className="bg-[#050505] border-[#2a2a2a]" />
              <p className="text-[10px] text-zinc-500 mt-1">Separate options with “/” or commas. Leave empty for no variants.</p>
            </Field>
            <Field label="Min Order Qty (à la carte)">
              <Input type="number" min="1" value={f.min_order_qty} onChange={(e) => setF({ ...f, min_order_qty: Math.max(1, Number(e.target.value) || 1) })} data-testid="mi-min-qty" className="bg-[#050505] border-[#2a2a2a]" />
              <p className="text-[10px] text-zinc-500 mt-1">Customers can't order fewer than this when adding the dish individually. 1 = no minimum.</p>
            </Field>
          </div>
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={f.is_active} onChange={(e) => setF({ ...f, is_active: e.target.checked })} className="accent-[#D4AF37]" />
            <span>Visible in order picker</span>
          </label>
          <div className="flex justify-end gap-2 pt-2">
            <Button onClick={onClose} variant="outline" className="border-[#2a2a2a] rounded-sm">Cancel</Button>
            <Button onClick={() => onSave({ ...f, variants: variantsText.split(/[,/]/).map((t) => t.trim()).filter(Boolean) })} disabled={!f.name} data-testid="mi-save" className="bg-[#D4AF37] text-black hover:bg-[#E5C86B] rounded-sm"><Save className="w-3.5 h-3.5 mr-1.5" />{f.id ? "Save Changes" : "Add Item"}</Button>
          </div>
        </div>
        {catModal && (
          <CategoryEditor
            category={catModal}
            onClose={() => setCatModal(null)}
            onSave={saveCategory}
            onDelete={catModal.id ? () => deleteCategory(catModal) : null}
          />
        )}
      </DialogContent>
    </Dialog>
  );
}

function CategoryEditor({ category, onClose, onSave, onDelete }) {
  const [f, setF] = useState({
    id: category.id,
    name_en: category.name_en || "",
    name_cn: category.name_cn || "",
    color: category.color || "#10B981",
    icon: category.icon || "Utensils",
    is_builtin: !!category.is_builtin,
  });
  return (
    <Dialog open={true} onOpenChange={(v) => !v && onClose?.()}>
      <DialogContent className="bg-[#0a0a0a] border-[#2a2a2a] text-white max-w-lg" data-testid="category-editor">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl">{f.id ? "Edit Category" : "New Category"}</DialogTitle>
          <DialogDescription className="sr-only">Add or edit a menu category. Categories group dishes in the catalog and order picker.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <Field label="Name · English"><Input value={f.name_en} onChange={(e) => setF({ ...f, name_en: e.target.value })} placeholder="e.g. Seafood Premium" data-testid="cat-name-en" className="bg-[#050505] border-[#2a2a2a]" /></Field>
            <Field label="Name · 中文"><Input value={f.name_cn} onChange={(e) => setF({ ...f, name_cn: e.target.value })} placeholder="例如: 海鲜特色" data-testid="cat-name-cn" className="bg-[#050505] border-[#2a2a2a]" /></Field>
          </div>
          <Field label="Color">
            <div className="flex items-center gap-2">
              <input type="color" value={f.color} onChange={(e) => setF({ ...f, color: e.target.value })} data-testid="cat-color" className="w-12 h-10 bg-transparent border border-[#2a2a2a] rounded-sm cursor-pointer" />
              <Input value={f.color} onChange={(e) => setF({ ...f, color: e.target.value })} className="bg-[#050505] border-[#2a2a2a] font-mono" />
            </div>
          </Field>
          <Field label="Icon">
            <div className="grid grid-cols-6 gap-2" data-testid="cat-icon-grid">
              {CAT_ICON_KEYS.map((k) => {
                const Cmp = CAT_ICONS[k];
                const selected = f.icon === k;
                return (
                  <button
                    key={k}
                    type="button"
                    onClick={() => setF({ ...f, icon: k })}
                    data-testid={`cat-icon-${k}`}
                    title={k}
                    className={`flex items-center justify-center h-10 rounded-sm border transition-colors ${selected ? "border-[#D4AF37] bg-[#D4AF37]/10 text-[#D4AF37]" : "border-[#2a2a2a] text-zinc-400 hover:border-[#3a3a3a]"}`}
                  >
                    <Cmp className="w-4 h-4" />
                  </button>
                );
              })}
            </div>
          </Field>
          {f.is_builtin && (
            <p className="text-[10px] text-zinc-500">This is a built-in category. You can rename, recolor or change its icon — but it can't be deleted.</p>
          )}
          <div className="flex justify-between items-center gap-2 pt-2">
            <div>
              {onDelete && !f.is_builtin && (
                <Button onClick={onDelete} variant="outline" data-testid="cat-delete" className="border-red-500/30 text-red-400 hover:bg-red-500/10 rounded-sm">
                  <Trash2 className="w-3.5 h-3.5 mr-1.5" /> Delete
                </Button>
              )}
            </div>
            <div className="flex gap-2">
              <Button onClick={onClose} variant="outline" className="border-[#2a2a2a] rounded-sm">Cancel</Button>
              <Button onClick={() => onSave(f)} disabled={!f.name_en} data-testid="cat-save" className="bg-[#D4AF37] text-black hover:bg-[#E5C86B] rounded-sm">
                <Save className="w-3.5 h-3.5 mr-1.5" />{f.id ? "Save Changes" : "Create Category"}
              </Button>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function PackagesTab({ canManage }) {
  const [items, setItems] = useState([]);
  const [packages, setPackages] = useState([]);
  const [categories, setCategories] = useState([]);
  const [editing, setEditing] = useState(null);
  const [q, setQ] = useState("");
  const [catFilter, setCatFilter] = useState("");
  const reload = async () => {
    const [pi, pp, pc] = await Promise.all([api.get("/menu/items"), api.get("/menu/packages"), api.get("/menu/categories")]);
    setItems(pi.data || []); setPackages(pp.data || []); setCategories(pc.data || []);
  };
  useEffect(() => { reload(); }, []);

  const save = async (p) => {
    try {
      const body = { ...p };
      if (p.id) await api.patch(`/menu/packages/${p.id}`, body);
      else await api.post("/menu/packages", body);
      toast.success(p.id ? "Package updated" : "Package added");
      setEditing(null); await reload();
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
  };
  const remove = async (p) => {
    if (!window.confirm(`Delete package "${p.name}"?`)) return;
    try { await api.delete(`/menu/packages/${p.id}`); toast.success("Deleted"); await reload(); }
    catch (e) { toast.error(e.response?.data?.detail || "Delete failed"); }
  };
  const itemNameMap = items.reduce((m, it) => { m[it.id] = it; return m; }, {});

  // Search: match package name / 中文 / description, or any dish name inside the package
  const ql = q.trim().toLowerCase();
  const visiblePkgs = (ql
    ? packages.filter((p) =>
        (p.name || "").toLowerCase().includes(ql) ||
        (p.name_cn || "").includes(q.trim()) ||
        (p.description || "").toLowerCase().includes(ql) ||
        (p.items || []).some((pi) => {
          const it = itemNameMap[pi.menu_item_id];
          return it && ((it.name || "").toLowerCase().includes(ql) || (it.name_cn || "").includes(q.trim()));
        }))
    : packages
  ).filter((p) => !catFilter || (p.category || "") === catFilter);
  const pkgCatNames = [...new Set(packages.map((p) => p.category).filter(Boolean))];

  const downloadPdf = () => {
    const overview = visiblePkgs.map((p) => [
      p.name + (p.name_cn ? ` (${p.name_cn})` : ""),
      p.category || "—",
      `${fmtCurrency(p.price_per_pax)} / ${p.pricing_mode === "set" ? "set" : "pax"}`,
      p.pricing_mode === "set" ? "—" : (p.min_pax > 0 ? `${p.min_pax} pax` : "—"),
      p.is_active === false ? "Hidden" : "Active",
    ]);
    const sections = [{
      title: "Packages Overview",
      columns: ["Package", "Category", "Price", "Min Pax", "Status"],
      rows: overview,
      columnStyles: { 0: { cellWidth: 62 }, 2: { halign: "right" } },
    }];
    visiblePkgs.forEach((p) => {
      const rows = [];
      (p.choice_slots || []).forEach((s) => {
        const dishes = (s.option_ids || []).map((id) => itemNameMap[id]?.name).filter(Boolean).join(", ");
        rows.push([s.label, s.selectable === false ? "Fixed" : "Choose 1", dishes || "—"]);
      });
      (p.items || []).forEach((pi) => {
        const it = itemNameMap[pi.menu_item_id];
        if (it) rows.push(["Included", "—", it.name]);
      });
      if (rows.length) {
        sections.push({
          title: `${p.name} — ${fmtCurrency(p.price_per_pax)}/${p.pricing_mode === "set" ? "set" : "pax"}`,
          columns: ["Section", "Type", "Dishes"],
          rows,
          columnStyles: { 0: { cellWidth: 40 }, 1: { cellWidth: 25 } },
        });
      }
    });
    downloadReport({
      title: "Package Catalog",
      filename: "package-catalog.pdf",
      subtitle: [catFilter ? `Category: ${catFilter}` : "All categories", ql ? `Search: "${q.trim()}"` : null].filter(Boolean).join(" · "),
      kpis: [
        { label: "Packages", value: visiblePkgs.length },
        { label: "Categories", value: pkgCatNames.length },
        { label: "Active", value: visiblePkgs.filter((p) => p.is_active !== false).length },
      ],
      sections,
    });
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3 flex-wrap">
        <div className="relative flex-1 min-w-[220px]">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500 pointer-events-none" />
          <Input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search package name / 中文 / dish inside…"
            data-testid="package-search"
            className="pl-9 bg-[#050505] border-[#2a2a2a]"
          />
        </div>
        <Button onClick={downloadPdf} variant="outline" data-testid="menu-packages-pdf" className="border-[#2a2a2a] hover:border-[#D4AF37] hover:text-[#D4AF37] rounded-sm">
          <FileDown className="w-3.5 h-3.5 mr-1.5" /> Download PDF
        </Button>
        {canManage && (
          <Button onClick={() => setEditing({ name: "", name_cn: "", description: "", pricing_mode: "pax", price_per_pax: 0, min_pax: 0, items: [], is_active: true })} data-testid="new-menu-package" className="bg-[#D4AF37] text-black hover:bg-[#E5C86B] rounded-sm">
            <Plus className="w-3.5 h-3.5 mr-1.5" /> New Package
          </Button>
        )}
      </div>
      {pkgCatNames.length > 0 && (
        <div className="flex items-center gap-1.5 flex-wrap" data-testid="package-cat-filter">
          <button onClick={() => setCatFilter("")} data-testid="package-cat-filter-all"
            className={`px-3 py-1.5 text-xs rounded-sm border transition-colors ${!catFilter ? "bg-[#D4AF37] text-black border-[#D4AF37]" : "border-[#2a2a2a] text-zinc-400 hover:border-[#D4AF37]"}`}>All</button>
          {pkgCatNames.map((c) => (
            <button key={c} onClick={() => setCatFilter(catFilter === c ? "" : c)} data-testid={`package-cat-filter-${c.replace(/\s+/g, "-")}`}
              className={`px-3 py-1.5 text-xs rounded-sm border transition-colors ${catFilter === c ? "bg-[#D4AF37] text-black border-[#D4AF37]" : "border-[#2a2a2a] text-zinc-400 hover:border-[#D4AF37]"}`}>{c}</button>
          ))}
        </div>
      )}
      <div className="grid md:grid-cols-2 gap-3">
        {visiblePkgs.map((p) => (
          <div key={p.id} className="card-lux p-6" data-testid={`menu-package-${p.id}`}>
            <div className="flex items-start justify-between">
              <div className="min-w-0">
                <div className="label-eyebrow text-[10px]">{p.category || p.name_cn || "Package"}</div>
                <h3 className="font-display text-2xl mt-1">{p.name}</h3>
                <p className="text-xs text-zinc-500 mt-1">{p.description || "—"}</p>
              </div>
              {canManage && (
                <div className="flex gap-1">
                  <Button onClick={() => setEditing(p)} variant="outline" size="sm" className="border-[#2a2a2a] hover:border-[#D4AF37] hover:text-[#D4AF37] rounded-sm h-7 w-7 p-0" data-testid={`menu-package-edit-${p.id}`}><Pencil className="w-3 h-3" /></Button>
                  <Button onClick={() => remove(p)} variant="outline" size="sm" className="border-red-500/30 text-red-400 hover:bg-red-500/10 rounded-sm h-7 w-7 p-0" data-testid={`menu-package-delete-${p.id}`}><Trash2 className="w-3 h-3" /></Button>
                </div>
              )}
            </div>
            <div className="mt-3 flex items-center gap-3 text-sm">
              <span className="text-[#D4AF37]">{fmtCurrency(p.price_per_pax)} <span className="text-xs text-zinc-500">/ {p.pricing_mode === "set" ? "set" : "pax"}</span></span>
              {p.pricing_mode !== "set" && p.min_pax > 0 && <span className="text-xs text-zinc-500">min {p.min_pax} pax</span>}
            </div>
            <div className="mt-3 space-y-1">
              <div className="label-eyebrow text-[10px] mb-1">Includes</div>
              {p.items.map((pi, idx) => {
                const it = itemNameMap[pi.menu_item_id];
                return (
                  <div key={idx} className="text-xs text-zinc-400">
                    · {it ? it.name : "(deleted item)"}{pi.quantity_factor !== 1 && <span className="text-zinc-600"> × {pi.quantity_factor}/pax</span>}
                  </div>
                );
              })}
              {p.items.length === 0 && <div className="text-xs text-zinc-600">No items yet</div>}
            </div>
          </div>
        ))}
        {packages.length === 0 && <div className="text-xs text-zinc-500 col-span-2 text-center py-12">No packages yet. Create one to bundle dishes for fast order setup.</div>}
        {packages.length > 0 && visiblePkgs.length === 0 && <div className="text-xs text-zinc-500 col-span-2 text-center py-12" data-testid="package-search-empty">No packages match "{q}"</div>}
      </div>
      {editing && <PackageEditor pkg={editing} allItems={items} categories={categories} onClose={() => setEditing(null)} onSave={save} />}
    </div>
  );
}

function PackageEditor({ pkg, allItems, categories = [], onClose, onSave }) {
  const [pkgCats, setPkgCats] = useState([]);
  const [catEdit, setCatEdit] = useState(null); // null | {mode:'new'|'rename', id?, name}
  const loadPkgCats = () => api.get("/menu/package-categories").then((r) => setPkgCats(r.data)).catch(() => {});
  useEffect(() => { loadPkgCats(); }, []);
  const savePkgCat = async () => {
    const name = (catEdit?.name || "").trim();
    if (!name) return;
    try {
      if (catEdit.mode === "rename") {
        await api.patch(`/menu/package-categories/${catEdit.id}`, { name });
        toast.success("Category renamed — all its packages updated");
      } else {
        await api.post("/menu/package-categories", { name });
        toast.success("Category added");
      }
      setF((prev) => ({ ...prev, category: name }));
      setCatEdit(null);
      loadPkgCats();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Save failed"); }
  };
  const deletePkgCat = async () => {
    const cur = pkgCats.find((c) => c.name === f.category);
    if (!cur) return;
    if (!window.confirm(`Delete category "${cur.name}"? A category still used by packages can't be deleted.`)) return;
    try {
      await api.delete(`/menu/package-categories/${cur.id}`);
      setF((prev) => ({ ...prev, category: "" }));
      loadPkgCats();
      toast.success("Category deleted");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Delete failed"); }
  };
  const [f, setF] = useState({
    id: pkg.id, name: pkg.name || "", name_cn: pkg.name_cn || "",
    description: pkg.description || "", category: pkg.category || "",
    pricing_mode: pkg.pricing_mode || "pax",
    price_per_pax: pkg.price_per_pax || 0,
    min_pax: pkg.min_pax || 0, items: pkg.items || [], menu_groups: pkg.menu_groups || [],
    group_items: pkg.group_items || {}, choice_slots: pkg.choice_slots || [],
    is_active: pkg.is_active !== false,
  });
  const [slotQ, setSlotQ] = useState({});
  const [dishQ, setDishQ] = useState("");
  const addItem = (menu_item_id) => {
    if (!menu_item_id) return;
    if (f.items.find((x) => x.menu_item_id === menu_item_id)) return;
    setF({ ...f, items: [...f.items, { menu_item_id, quantity_factor: 1 }] });
  };
  const removeItem = (mid) => setF({ ...f, items: f.items.filter((x) => x.menu_item_id !== mid) });
  const setFactor = (mid, q) => setF({ ...f, items: f.items.map((x) => x.menu_item_id === mid ? { ...x, quantity_factor: q } : x) });

  // Menu Groups — customer picks ONE dish per included group (options auto-follow the menu DB)
  const toggleGroup = (key) => setF({
    ...f,
    menu_groups: f.menu_groups.includes(key) ? f.menu_groups.filter((k) => k !== key) : [...f.menu_groups, key],
  });
  const toggleGroupDish = (key, itemId) => setF((prev) => {
    const cur = prev.group_items[key] || [];
    const next = cur.includes(itemId) ? cur.filter((x) => x !== itemId) : [...cur, itemId];
    return { ...prev, group_items: { ...prev.group_items, [key]: next } };
  });

  // Custom sections — fully manual: own title + hand-picked dishes (titles may repeat)
  const addSlot = () => setF((prev) => ({ ...prev, choice_slots: [...prev.choice_slots, { label: "", option_ids: [], selectable: true }] }));
  const removeSlot = (i) => setF((prev) => ({ ...prev, choice_slots: prev.choice_slots.filter((_, idx) => idx !== i) }));
  const setSlotLabel = (i, label) => setF((prev) => ({ ...prev, choice_slots: prev.choice_slots.map((s, idx) => idx === i ? { ...s, label } : s) }));
  const setSlotSelectable = (i, val) => setF((prev) => ({ ...prev, choice_slots: prev.choice_slots.map((s, idx) => idx === i ? { ...s, selectable: val } : s) }));
  const toggleSlotOption = (i, optId) => setF((prev) => ({
    ...prev,
    choice_slots: prev.choice_slots.map((s, idx) => {
      if (idx !== i) return s;
      const has = s.option_ids.includes(optId);
      return { ...s, option_ids: has ? s.option_ids.filter((x) => x !== optId) : [...s.option_ids, optId] };
    }),
  }));

  const dq = dishQ.trim().toLowerCase();
  const pickable = allItems
    .filter((it) => !f.items.find((x) => x.menu_item_id === it.id))
    .filter((it) => !dq ||
      (it.name || "").toLowerCase().includes(dq) ||
      (it.name_cn || "").includes(dishQ.trim()) ||
      (it.category || "").toLowerCase().includes(dq));

  return (
    <Dialog open={true} onOpenChange={(v) => !v && onClose?.()}>
      <DialogContent className="bg-[#0a0a0a] border-[#2a2a2a] text-white max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl">{f.id ? "Edit Package" : "New Package"}</DialogTitle>
          <DialogDescription className="sr-only">Build a reusable bundle of menu items for fast order creation.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <Field label="Name · English"><Input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} data-testid="pkg-name" className="bg-[#050505] border-[#2a2a2a]" /></Field>
            <Field label="Name · 中文"><Input value={f.name_cn} onChange={(e) => setF({ ...f, name_cn: e.target.value })} data-testid="pkg-name-cn" className="bg-[#050505] border-[#2a2a2a]" /></Field>
            <div className="col-span-2">
              <Field label="Pricing Mode">
                <div className="flex gap-1.5">
                  <button type="button" onClick={() => setF({ ...f, pricing_mode: "pax" })} data-testid="pkg-mode-pax"
                    className={`px-3 py-2 text-xs rounded-sm border transition-colors ${f.pricing_mode !== "set" ? "bg-[#D4AF37] text-black border-[#D4AF37]" : "border-[#2a2a2a] text-zinc-400 hover:border-[#D4AF37]"}`}>
                    Per Pax — price × number of pax
                  </button>
                  <button type="button" onClick={() => setF({ ...f, pricing_mode: "set" })} data-testid="pkg-mode-set"
                    className={`px-3 py-2 text-xs rounded-sm border transition-colors ${f.pricing_mode === "set" ? "bg-[#D4AF37] text-black border-[#D4AF37]" : "border-[#2a2a2a] text-zinc-400 hover:border-[#D4AF37]"}`}>
                    Per Set — fixed price, customer picks sets
                  </button>
                </div>
              </Field>
            </div>
            <Field label={f.pricing_mode === "set" ? "Price per Set (RM)" : "Price per Pax (RM)"}><Input type="number" step="0.01" value={f.price_per_pax} onChange={(e) => setF({ ...f, price_per_pax: Number(e.target.value) })} data-testid="pkg-price" className="bg-[#050505] border-[#2a2a2a]" /></Field>
            {f.pricing_mode !== "set" ? (
              <Field label="Min Pax"><Input type="number" value={f.min_pax} onChange={(e) => setF({ ...f, min_pax: Number(e.target.value) })} data-testid="pkg-min-pax" className="bg-[#050505] border-[#2a2a2a]" /></Field>
            ) : <div />}
            <div className="col-span-2">
              <Field label="Package Category (shown as a group to customers)">
                <div className="flex items-stretch gap-1">
                  <select value={f.category || ""} data-testid="pkg-category"
                    onChange={(e) => { if (e.target.value === "__new__") { setCatEdit({ mode: "new", name: "" }); return; } setF({ ...f, category: e.target.value }); }}
                    className="flex-1 bg-[#050505] border border-[#2a2a2a] rounded-sm px-3 h-10 text-sm">
                    <option value="">— No category —</option>
                    {pkgCats.map((c) => <option key={c.id} value={c.name}>{c.name}</option>)}
                    {f.category && !pkgCats.find((c) => c.name === f.category) && <option value={f.category}>{f.category}</option>}
                    <option value="__new__">＋ New category…</option>
                  </select>
                  {f.category && pkgCats.find((c) => c.name === f.category) && (
                    <>
                      <Button type="button" onClick={() => setCatEdit({ mode: "rename", id: pkgCats.find((c) => c.name === f.category).id, name: f.category })} variant="outline" data-testid="pkg-category-rename" title="Rename this category"
                        className="h-10 w-10 p-0 border-[#2a2a2a] hover:border-[#D4AF37] hover:text-[#D4AF37] rounded-sm"><Pencil className="w-3.5 h-3.5" /></Button>
                      <Button type="button" onClick={deletePkgCat} variant="outline" data-testid="pkg-category-delete" title="Delete this category"
                        className="h-10 w-10 p-0 border-[#2a2a2a] hover:border-red-500 hover:text-red-400 rounded-sm"><Trash2 className="w-3.5 h-3.5" /></Button>
                    </>
                  )}
                </div>
                {catEdit && (
                  <div className="mt-1.5">
                    <div className="flex items-center gap-1.5">
                      <Input autoFocus value={catEdit.name} onChange={(e) => setCatEdit({ ...catEdit, name: e.target.value })}
                        placeholder={catEdit.mode === "rename" ? "New name" : "e.g. Vegetarian Menu"} data-testid="pkg-category-name-input"
                        className="h-8 bg-[#050505] border-[#2a2a2a] text-sm" />
                      <Button type="button" onClick={savePkgCat} data-testid="pkg-category-save" className="h-8 bg-[#D4AF37] text-black hover:bg-[#E5C86B] rounded-sm text-xs px-3">{catEdit.mode === "rename" ? "Rename" : "Add"}</Button>
                      <Button type="button" onClick={() => setCatEdit(null)} variant="outline" className="h-8 border-[#2a2a2a] rounded-sm text-xs px-3">Cancel</Button>
                    </div>
                    {catEdit.mode === "rename" && <p className="text-[10px] text-zinc-500 mt-1">Renaming updates every package using this category.</p>}
                  </div>
                )}
              </Field>
            </div>
          </div>
          <Field label="Description">
            <Input value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} placeholder="e.g. 4 mains + dessert + drinks" className="bg-[#050505] border-[#2a2a2a]" />
          </Field>
          <div>
            <Label className="label-eyebrow text-[10px]">Items in this Package (internal — kitchen prep & costing only, not shown to customers)</Label>
            <div className="mt-2 space-y-1 max-h-60 overflow-y-auto pr-1">
              {f.items.map((pi) => {
                const it = allItems.find((x) => x.id === pi.menu_item_id);
                return (
                  <div key={pi.menu_item_id} className="flex items-center gap-2 px-3 py-1.5 rounded-sm bg-[#050505] border border-[#1a1a1a]" data-testid={`pkg-row-${pi.menu_item_id}`}>
                    <span className="flex-1 text-sm">{it?.name || "(deleted)"} <span className="text-[10px] text-zinc-500">{it?.category}</span></span>
                    <Input type="number" step="0.1" min="0" value={pi.quantity_factor} onChange={(e) => setFactor(pi.menu_item_id, Number(e.target.value))} className="w-20 h-7 bg-[#0a0a0a] border-[#2a2a2a] text-xs" title="Quantity per pax" />
                    <button onClick={() => removeItem(pi.menu_item_id)} className="text-zinc-500 hover:text-red-400"><X className="w-4 h-4" /></button>
                  </div>
                );
              })}
              {f.items.length === 0 && <div className="text-xs text-zinc-600 py-2">No items added yet</div>}
            </div>
            <div className="mt-2 space-y-1.5">
              <div className="relative">
                <Search className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-zinc-500 pointer-events-none" />
                <Input
                  value={dishQ}
                  onChange={(e) => setDishQ(e.target.value)}
                  placeholder="Search dish to add… (name / 中文 / category)"
                  data-testid="pkg-dish-search"
                  className="pl-8 h-8 text-xs bg-[#050505] border-[#2a2a2a]"
                />
              </div>
              <select onChange={(e) => { addItem(e.target.value); e.target.value = ""; }} data-testid="pkg-add-item" className="w-full bg-[#050505] border border-[#2a2a2a] rounded-sm px-3 h-9 text-sm">
                <option value="">{pickable.length ? "+ Add item to package…" : "— No dish matches search —"}</option>
                {pickable.map((it) => (
                  <option key={it.id} value={it.id}>{it.name}{it.name_cn ? ` (${it.name_cn})` : ""} ({it.category} · RM{it.unit_price})</option>
                ))}
              </select>
            </div>
          </div>

          <div className="border-t border-[#1a1a1a] pt-3">
            <Label className="label-eyebrow text-[10px]">Menu Groups — customer selection</Label>
            <p className="text-[10px] text-zinc-500 mt-0.5 mb-2">Tick the groups included in this package — customers choose ONE dish per group. Then optionally tick specific dishes to limit the choices; leave dishes unticked to offer every available dish in that group automatically.</p>
            <div className="space-y-2" data-testid="pkg-menu-groups">
              {categories.map((c) => {
                const groupDishes = allItems.filter((it) => it.category === c.key && it.is_active !== false);
                const included = f.menu_groups.includes(c.key);
                const manual = f.group_items[c.key] || [];
                return (
                  <div key={c.key} className={`rounded-sm border transition-colors ${included ? "border-[#D4AF37]/60" : "border-[#2a2a2a]"}`}>
                    <label className={`flex items-center gap-2 p-2.5 text-xs cursor-pointer ${included ? "text-[#D4AF37]" : "text-zinc-400 hover:text-zinc-200"}`} data-testid={`pkg-group-${c.key}`}>
                      <input type="checkbox" checked={included} onChange={() => toggleGroup(c.key)} className="accent-[#D4AF37]" />
                      <span className="flex-1">{c.name_en}{c.name_cn ? ` ${c.name_cn}` : ""} <span className="text-zinc-600">({groupDishes.length} dishes)</span></span>
                      {included && <span className="text-[10px] text-zinc-500">{manual.length > 0 ? `${manual.length} selected manually` : "all dishes offered"}</span>}
                    </label>
                    {included && groupDishes.length > 0 && (
                      <div className="px-3 pb-2.5 grid grid-cols-2 sm:grid-cols-3 gap-1 border-t border-[#1a1a1a] pt-2" data-testid={`pkg-group-dishes-${c.key}`}>
                        {groupDishes.map((it) => (
                          <label key={it.id} className={`flex items-center gap-1.5 text-[11px] cursor-pointer ${manual.includes(it.id) ? "text-[#D4AF37]" : "text-zinc-500 hover:text-zinc-300"}`}>
                            <input type="checkbox" checked={manual.includes(it.id)} onChange={() => toggleGroupDish(c.key, it.id)} className="accent-[#D4AF37]" data-testid={`pkg-gd-${c.key}-${it.id}`} />
                            <span className="truncate">{it.name}</span>
                          </label>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
            {f.menu_groups.length > 0 && <div className="text-[10px] text-emerald-400 mt-1.5">{f.menu_groups.length} groups — customers make {f.menu_groups.length} selections</div>}
          </div>

          <div className="border-t border-[#1a1a1a] pt-3">
            <div className="flex items-center justify-between mb-1">
              <Label className="label-eyebrow text-[10px]">Custom Sections (manual)</Label>
              <Button type="button" onClick={addSlot} variant="outline" size="sm" data-testid="pkg-add-section" className="border-[#2a2a2a] rounded-sm h-7 text-xs"><Plus className="w-3 h-3 mr-1" /> Add Section</Button>
            </div>
            <p className="text-[10px] text-zinc-500 mb-2">Build your own sections with any title and hand-picked dishes (titles can repeat, e.g. two "Appetizer" sections). Customers choose ONE dish per section. <span className="text-amber-400">If any custom sections exist, they are shown INSTEAD of the Menu Groups above.</span></p>
            <div className="space-y-3">
              {f.choice_slots.map((slot, i) => {
                const q = (slotQ[i] || "").toLowerCase();
                const list = allItems.filter((it) => it.is_active !== false && (!q || (it.name || "").toLowerCase().includes(q) || (it.name_cn || "").includes(slotQ[i] || "") || (it.category || "").toLowerCase().includes(q)));
                return (
                  <div key={i} className="p-3 rounded-sm bg-[#050505] border border-[#1a1a1a]" data-testid={`pkg-section-${i}`}>
                    <div className="flex items-center gap-2 mb-2">
                      <span className="text-[10px] text-zinc-500 shrink-0">Section {i + 1}</span>
                      <Input value={slot.label} onChange={(e) => setSlotLabel(i, e.target.value)} placeholder="Title e.g. Appetizer" data-testid={`pkg-section-title-${i}`} className="flex-1 h-8 bg-[#0a0a0a] border-[#2a2a2a] text-sm" />
                      <button type="button" onClick={() => setSlotSelectable(i, !(slot.selectable !== false))} data-testid={`pkg-section-selectable-${i}`}
                        className={`shrink-0 text-[10px] px-2 py-1 rounded-sm border transition-colors ${slot.selectable !== false ? "border-[#D4AF37] text-[#D4AF37] bg-[#D4AF37]/10" : "border-[#2a2a2a] text-zinc-400 bg-[#0a0a0a]"}`}>
                        {slot.selectable !== false ? "Selectable" : "Fixed"}
                      </button>
                      <button type="button" onClick={() => removeSlot(i)} data-testid={`pkg-section-remove-${i}`} className="text-zinc-500 hover:text-red-400"><X className="w-4 h-4" /></button>
                    </div>
                    <p className="text-[10px] text-zinc-600 mb-1.5">{slot.selectable !== false ? "Customer picks ONE dish from this section." : "Fixed — all dishes below are included, no customer choice."}</p>
                    <Input value={slotQ[i] || ""} onChange={(e) => setSlotQ({ ...slotQ, [i]: e.target.value })} placeholder="Search dishes…" className="h-7 mb-1.5 bg-[#0a0a0a] border-[#2a2a2a] text-xs" />
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-1 max-h-36 overflow-y-auto">
                      {list.map((it) => (
                        <label key={it.id} className={`flex items-center gap-1.5 text-[11px] cursor-pointer ${slot.option_ids.includes(it.id) ? "text-[#D4AF37]" : "text-zinc-500 hover:text-zinc-300"}`}>
                          <input type="checkbox" checked={slot.option_ids.includes(it.id)} onChange={() => toggleSlotOption(i, it.id)} className="accent-[#D4AF37]" data-testid={`pkg-section-dish-${i}-${it.id}`} />
                          <span className="truncate">{it.name} <span className="text-zinc-600">·{it.category}</span></span>
                        </label>
                      ))}
                    </div>
                    <div className="text-[10px] text-zinc-500 mt-1">{slot.option_ids.length} dishes in this section</div>
                  </div>
                );
              })}
              {f.choice_slots.length === 0 && <div className="text-xs text-zinc-600 py-1">No custom sections — Menu Groups above (if any) will be used.</div>}
            </div>
          </div>

          <div className="flex justify-end gap-2 pt-2">
            <Button onClick={onClose} variant="outline" className="border-[#2a2a2a] rounded-sm">Cancel</Button>
            <Button onClick={() => onSave(f)} disabled={!f.name} data-testid="pkg-save" className="bg-[#D4AF37] text-black hover:bg-[#E5C86B] rounded-sm"><Save className="w-3.5 h-3.5 mr-1.5" />{f.id ? "Save Changes" : "Create Package"}</Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function Field({ label, children }) {
  return (
    <div className="space-y-1.5">
      <Label className="label-eyebrow text-[10px]">{label}</Label>
      {children}
    </div>
  );
}
